import React from 'react';
import { motion } from 'framer-motion';
import { Check, ArrowRight } from 'lucide-react';
import SectionHeading from '@/components/SectionHeading';
import { useInView } from '@/lib/hooks';
import { PRICING } from '@/constants/testIds';

const TIERS = [
  {
    key: 'starter',
    name: 'Starter',
    price: '₹3,999',
    sub: 'For businesses that need a strong online presence — fast.',
    features: [
      'Single-page landing site',
      'Mobile-responsive design',
      'Contact form',
      '3 sections of your choice',
      'Delivered in 3 days',
    ],
    cta: 'Get Started',
    days: ['Day 1', 'Day 2', 'Day 3'],
  },
  {
    key: 'growth',
    name: 'Growth',
    price: '₹8,999',
    sub: 'For businesses serious about growth and credibility.',
    features: [
      'Up to 5 pages',
      'SEO setup (meta, sitemap, speed)',
      'CMS integration (edit content yourself)',
      'Google Analytics',
      'Delivered in 5–7 days',
    ],
    cta: 'Get Started',
    popular: true,
    days: ['Day 1', 'Day 2–3', 'Day 4', 'Day 5', 'Launch'],
  },
  {
    key: 'premium',
    name: 'Premium',
    price: '₹15,999+',
    sub: 'For businesses that want to dominate their category online.',
    features: [
      'Custom animations & micro-interactions',
      'E-commerce / booking system',
      'Advanced integrations',
      'Priority support (3 months)',
      'Dedicated project manager',
    ],
    cta: 'Let’s Talk',
    days: ['Discovery', 'Build', 'Refine', 'QA', 'Launch'],
  },
];

function MiniTimeline({ days }) {
  return (
    <div className="mt-5 pt-5 border-t border-[color:var(--sh-line-2)]">
      <div className="text-[10px] font-accent uppercase tracking-[0.22em] text-[color:var(--sh-muted)] mb-2.5">Delivery Timeline</div>
      <div className="flex items-center justify-between gap-1">
        {days.map((d, i) => (
          <React.Fragment key={d + i}>
            <div className="flex flex-col items-center gap-1 flex-1">
              <div className="w-1.5 h-1.5 rounded-full bg-[color:var(--sh-amber)]" />
              <span className="text-[10px] text-[color:var(--sh-muted)] font-accent">{d}</span>
            </div>
            {i < days.length - 1 && <div className="flex-1 h-px bg-[color:var(--sh-line)]" />}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
}

function TierCard({ t, i, inView }) {
  return (
    <motion.div
      data-testid={PRICING.tier(t.key)}
      initial={{ opacity: 0, y: 18 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.5, delay: 0.1 + i * 0.1 }}
      className={`card-light p-7 relative ${t.popular ? 'tier-popular' : ''}`}
    >
      {t.popular && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full text-[10px] font-accent uppercase tracking-[0.22em] bg-[color:var(--sh-amber)] text-white font-semibold">
          Most Popular
        </div>
      )}
      <div className="font-accent text-xs uppercase tracking-[0.22em] text-[color:var(--sh-muted)]">{t.name}</div>
      <div className="mt-2 font-display text-4xl md:text-[2.6rem] font-semibold text-[color:var(--sh-ink)]">{t.price}</div>
      <p className="mt-3 text-[14px] text-[color:var(--sh-muted)] leading-relaxed">{t.sub}</p>
      <ul className="mt-6 space-y-2.5">
        {t.features.map((f) => (
          <li key={f} className="flex items-start gap-2.5 text-[14px] text-[color:var(--sh-ink-soft)]">
            <Check size={15} className="text-[color:var(--sh-amber-2)] shrink-0 mt-0.5" />
            <span>{f}</span>
          </li>
        ))}
      </ul>
      <MiniTimeline days={t.days} />
      <button
        onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
        className={`mt-6 w-full inline-flex items-center justify-center gap-2 ${t.popular ? 'btn-amber' : 'btn-ghost'}`}
      >
        {t.cta} <ArrowRight size={14} />
      </button>
    </motion.div>
  );
}

export default function Pricing() {
  const [ref, inView] = useInView({ threshold: 0.15, once: true });
  return (
    <section id="pricing" className="section" data-testid={PRICING.block}>
      <div className="container-pro">
        <SectionHeading
          eyebrow="PRICING"
          title="No hidden costs. No surprise bills. Just great websites."
          subtitle="Pick the plan that fits where your business is today — upgrade anytime."
        />
        <div ref={ref} className="grid grid-cols-1 md:grid-cols-3 gap-5 items-stretch">
          <div className="order-2 md:order-1"><TierCard t={TIERS[0]} i={0} inView={inView} /></div>
          <div className="order-1 md:order-2"><TierCard t={TIERS[1]} i={1} inView={inView} /></div>
          <div className="order-3"><TierCard t={TIERS[2]} i={2} inView={inView} /></div>
        </div>
      </div>
    </section>
  );
}
